import React, { useState } from 'react';
import PageHeader from '../components/PageHeader';
import './WorksPage.css';

interface Work {
  title: string;
  subtitle: string;
  category: string;
  description: string;
  image: string;
  external: string;
}

const works: Work[] = [
  {
    title: 'وسم',
    subtitle: 'مشروع شخصي',
    category: 'SaaS',
    description: 'مولّد ملفات شخصية بالذكاء الاصطناعي — ارفع سيرتك الذاتية وأنشئ صفحة احترافية في دقائق',
    image: '/images/projects/wasm.webp',
    external: 'https://usewasm.com/',
  },
  {
    title: 'Picssync',
    subtitle: 'مشروع شخصي',
    category: 'SaaS',
    description: 'أتمتة توزيع صور المصورين بذكاء على العملاء عبر روابط آمنة',
    image: '/images/projects/picssync.webp',
    external: 'https://picssync.ftq.sa',
  },
  {
    title: 'لمّة',
    subtitle: 'مشروع شخصي',
    category: 'منصة ألعاب',
    description: 'منصة ألعاب جماعية تفاعلية بالوقت الحقيقي للعائلة والأصدقاء',
    image: '/images/projects/lamma.webp',
    external: 'https://lamma.ftq.sa',
  },
  {
    title: 'وريث',
    subtitle: 'فريق التراث التطوعي',
    category: 'منصة تراثية',
    description: 'منصة ويب متكاملة لإدارة التراث التطوعي مع لوحة تحكم شاملة',
    image: '/images/projects/wareeth.webp',
    external: 'https://wareeth.org',
  },
  {
    title: 'شركة العماد',
    subtitle: 'مكتب محاماة',
    category: 'قانوني',
    description: 'صفحة هبوط احترافية لمكتب محاماة بهوية قانونية رسمية',
    image: '/images/projects/imd.webp',
    external: 'https://imd-tau.vercel.app/',
  },
  {
    title: 'درع الأمان',
    subtitle: 'SAFCSP — مشروع تدريبي',
    category: 'تعليمي',
    description: 'تعليم الأمن السيبراني للأطفال بطريقة تفاعلية وممتعة',
    image: '/images/projects/darae-alamaan.webp',
    external: 'https://darae-alamaan.vercel.app',
  },
  {
    title: 'Day Day Game',
    subtitle: 'مجمع ترفيهي — الرياض',
    category: 'موقع تجاري',
    description: 'موقع متكامل لمجمع ترفيهي مع بوابة دفع إلكتروني',
    image: '/images/projects/daydaygame.webp',
    external: 'https://daydaygame.sa/',
  },
];

const filters = ['الكل', 'منصة', 'SaaS', 'موقع تجاري', 'تعليمي', 'قانوني'];

const matchesFilter = (work: Work, filter: string) => {
  if (filter === 'الكل') return true;
  if (filter === 'منصة') return work.category.includes('منصة');
  return work.category === filter;
};

const WorksPage: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('الكل');
  const filtered = works.filter((w) => matchesFilter(w, activeFilter));

  return (
    <section className="works-page">
      <div className="works-page-inner">
        <PageHeader
          title="جميع الأعمال"
          subtitle="مجموعة من المشاريع التي بنيتها من الفكرة حتى الإطلاق — كل مشروع قصة حل حقيقية."
          icon={
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="3" width="12" height="10" rx="1.5" stroke="white" strokeWidth="1.33" />
              <path d="M5 6H11M5 8.5H11M5 11H8" stroke="white" strokeWidth="1.33" strokeLinecap="round" />
            </svg>
          }
        />

        <div className="works-filters">
          {filters.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setActiveFilter(f)}
              className={`works-filter ${activeFilter === f ? 'is-active' : ''}`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="works-grid">
          {filtered.map((work) => (
            <a
              key={work.title}
              href={work.external}
              target="_blank"
              rel="noopener noreferrer"
              className="work-card-large"
            >
              <div
                className="work-card-large-image"
                style={{ backgroundImage: `url(${work.image})` }}
                role="img"
                aria-label={work.title}
              >
                <span className="work-card-large-category">{work.category}</span>
              </div>
              <div className="work-card-large-footer">
                <div className="work-card-large-meta">
                  <div className="work-card-large-icon-bubble">
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                      <path d="M3.5 9.5L9.5 3.5M9.5 3.5H4.5M9.5 3.5V8.5" stroke="#0369A1" strokeWidth="1.08" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="work-card-large-text">
                    <h3>{work.title}</h3>
                    <p className="work-card-large-description">{work.description}</p>
                  </div>
                </div>
                <span className="work-card-large-subtitle">{work.subtitle}</span>
              </div>
            </a>
          ))}
        </div>

        <p className="works-private-note">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="4" y="10" width="16" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <span>وهناك مشاريع أخرى لا يمكن عرضها لكونها تحت اتفاقية سرية مع عملائها.</span>
        </p>
      </div>
    </section>
  );
};

export default WorksPage;
